import React, {useEffect, useRef, useState} from 'react';
import {Button, FlexRow} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {FEATURE_FLAGS} from '../../../../../constants';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {UNHANDLED} from '../clientAssetMutations';
import {
	CANCELLED,
	CONNECT_FAILED,
	PICKER_FAILED,
	PROVIDER_KEY,
	requestCloudCode,
	type CloudProvider,
	type CloudProviderConfig,
	type PickedCloudFile,
	type PickedFile,
} from './cloudConnect';
import {connectCloudStorage, issuePickerToken, useCloudProviders} from './cloudProviderQueries';
import {pickGoogleDriveFile} from './googleDrivePicker';
import {pickOneDriveFile} from './oneDrivePicker';

type Props = {
	onPicked: (pick: PickedCloudFile) => void;
	/** A translation id to show inline, or null to clear it. */
	onError: (messageId: string | null) => void;
	/** Offer only this provider (a new version of a linked asset stays on its provider). */
	only?: CloudProvider;
	/** The parent's in-flight flag. */
	disabled?: boolean;
	/** Reports a connect or pick in flight, so the parent can hold its own actions until it lands. */
	onBusyChange?: (busy: boolean) => void;
	/** Rendered instead when there is no provider to offer (flag off, none configured, no picker). */
	fallback?: React.ReactNode;
};

// Providers with a picker in this client.
const PICKABLE: CloudProvider[] = ['GOOGLE_DRIVE', 'ONEDRIVE'];
const TRANSLATION_ID = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/;
// Refusals that mean the stored grant is gone: refresh, so the button offers Reconnect.
const RECONNECT_IDS = ['client_assets.error.cloud_auth_revoked', 'client_assets.error.cloud_not_connected'];

/**
 * The inline message for a failed connect or pick, or null when there is nothing to add: a
 * cancel is the PM's own choice, and UNHANDLED has already been explained by the framework modal.
 */
const messageIdFor = (error: unknown, fallback: string): string | null => {
	const message = error instanceof Error ? error.message : '';
	if (message === CANCELLED || message === UNHANDLED) return null;
	return TRANSLATION_ID.test(message) ? message : fallback;
};

type Action = 'connect' | 'reconnect' | 'link';

const actionFor = (config: CloudProviderConfig): Action => {
	if (!config.connection) return 'connect';
	return config.connection.status === 'revoked' ? 'reconnect' : 'link';
};

/**
 * "Link from <provider>" for the client-asset forms, behind client_workspace_cloud_links: connects
 * the PM's storage account first when needed, then opens the provider's picker and hands the
 * picked file back. Renders (and fetches) nothing but `fallback` with the flag off.
 */
export const CloudLinkButton = ({
	onPicked,
	onError,
	only,
	disabled = false,
	onBusyChange,
	fallback = null,
}: Props): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const enabled = hasFeatureFlag(FEATURE_FLAGS.CLIENT_WORKSPACE_CLOUD_LINKS);
	const {providers, settled, refresh} = useCloudProviders(enabled);
	const [busy, setBusy] = useState(false);
	// State alone cannot stop a second click landing before the disabled render commits.
	const busyRef = useRef(false);
	// A popup or the picker can answer after the form that owns this button has closed.
	const mountedRef = useRef(true);
	useEffect(
		() => () => {
			mountedRef.current = false;
		},
		[]
	);

	const start = (): boolean => {
		if (busyRef.current) return false;
		busyRef.current = true;
		setBusy(true);
		onBusyChange?.(true);
		onError(null);
		return true;
	};
	const finish = () => {
		busyRef.current = false;
		if (!mountedRef.current) return;
		setBusy(false);
		onBusyChange?.(false);
	};

	const fail = (error: unknown, fallback: string) => {
		if (!mountedRef.current) return;
		const id = messageIdFor(error, fallback);
		if (id) onError(id);
		if (id && RECONNECT_IDS.includes(id)) refresh();
	};

	// No await before requestCloudCode: the popup must open inside the click or it is blocked.
	const connect = (config: CloudProviderConfig) => {
		if (!start()) return;
		const code = requestCloudCode(config);
		code.then(authCode => connectCloudStorage({provider: config.provider, code: authCode}))
			.then(() => mountedRef.current && refresh())
			.catch(error => fail(error, CONNECT_FAILED))
			.finally(finish);
	};

	/** Opens this provider's picker with fresh, short-lived, server-minted tokens. */
	const pickFile = async (config: CloudProviderConfig): Promise<PickedFile | null> => {
		// OneDrive opens its popup before anything is awaited, so this branch must stay first.
		if (config.provider === 'ONEDRIVE') {
			return pickOneDriveFile(resource =>
				issuePickerToken(resource ? {provider: config.provider, resource} : {provider: config.provider}).then(
					token => token.accessToken
				)
			);
		}
		// Checked before minting, so a misconfigured provider costs no token refresh.
		const {pickerApiKey, appId} = config;
		if (!pickerApiKey || !appId) throw new Error(PICKER_FAILED);
		const {accessToken} = await issuePickerToken({provider: config.provider});
		return pickGoogleDriveFile({accessToken, developerKey: pickerApiKey, appId});
	};

	const link = async (config: CloudProviderConfig) => {
		if (!start()) return;
		try {
			const picked = await pickFile(config);
			if (picked && mountedRef.current) onPicked({provider: config.provider, ...picked});
		} catch (error) {
			fail(error, PICKER_FAILED);
		} finally {
			finish();
		}
	};

	if (!enabled) return <>{fallback}</>;
	// Nothing until the lookup answers, so the fallback does not flash up and vanish.
	if (!settled) return null;
	const offered = providers.filter(p => PICKABLE.includes(p.provider) && (!only || p.provider === only));
	if (offered.length === 0) return <>{fallback}</>;

	return (
		<FlexRow gap="s">
			{offered.map(config => {
				const action = actionFor(config);
				return (
					<Button
						key={config.provider}
						emphasis="low"
						onPress={() => (action === 'link' ? link(config) : connect(config))}
						disabled={disabled || busy}
						data-cy={`client-asset-cloud-${action}-${PROVIDER_KEY[config.provider]}`}
					>
						{formatMessage({id: `client_assets.cloud.${action}.${PROVIDER_KEY[config.provider]}`})}
					</Button>
				);
			})}
		</FlexRow>
	);
};
